import LoadingButton from '@app/src/components/LoadingButton'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import AiScreeningLimitAlert from '@app/src/pages/Configurations/ConfigurationsPages/AiScreening/AiScreeningLimitAlert'
import { palette } from '@app/src/theme/palette'
import { validURL } from '@app/src/utils/helpers'
import { AutoAwesomeOutlined, DescriptionOutlined, InfoOutlined } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  alpha,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import ProvidersAccordionConditionalCheck from './ProvidersAccordionConditionalCheck'
import type { ScheduleScreeningFormData } from './ScheduleProviderScreeningModal'
import { SCHEDULE_SCREENING_QUESTION_KEYS } from './ScheduleProviderScreeningModal'

type ScheduleProviderScreeningSummaryProps = {
  totalScreeningsToPerform: number
  remainingScreeningsEnough: boolean
  remainingProviderScreenings: number
  sendEmailOnComplete: boolean
  onEmailToggle: (enabled: boolean) => void
  isLoading: boolean
  onSubmit: () => void
}

const ScheduleProviderScreeningSummary: React.FC<ScheduleProviderScreeningSummaryProps> = ({
  totalScreeningsToPerform,
  remainingScreeningsEnough,
  remainingProviderScreenings,
  sendEmailOnComplete,
  onEmailToggle,
  isLoading,
  onSubmit,
}) => {
  const { formatMessage } = useIntl()
  const { providersToConnect } = useProvidersModal()
  const { watch, setValue, getValues } = useFormContext<ScheduleScreeningFormData>()

  const sortedProviders = React.useMemo(() => {
    if (!providersToConnect) return []
    return providersToConnect.sort((a, b) => {
      const aWebsiteValid = validURL(a.websiteAddress) ? 0 : 1
      const bWebsiteValid = validURL(b.websiteAddress) ? 0 : 1
      return bWebsiteValid - aWebsiteValid
    })
  }, [providersToConnect])

  const watchProviders = watch(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS)
  const templates = getValues(SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES)

  const handleChangeSetFormValues = (providerId: number) => {
    const toBeChecked = !watchProviders.includes(providerId)

    if (toBeChecked) {
      setValue(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS, Array.from(new Set([...watchProviders, providerId])))
    } else {
      setValue(
        SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS,
        watchProviders.filter(watchProviderId => watchProviderId !== providerId),
      )
    }
  }

  // The drawer keeps this callback from when it opened, so read the selection at save time
  const selectProvider = (providerId: number) => {
    const selectedProviderIds = getValues(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS)
    setValue(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS, Array.from(new Set([...selectedProviderIds, providerId])))
  }

  return (
    <Stack flexGrow={1} spacing={2}>
      <Typography variant="subtitle1" gutterBottom>
        {formatMessage({ id: 'form.scheduleProviderScreening.summary.companies' })}
      </Typography>
      <ProvidersAccordionConditionalCheck
        title={formatMessage(
          { id: 'form.scheduleProviderScreening.summary.companiesSelected' },
          { count: watchProviders.length },
        )}
        providers={sortedProviders ?? []}
        selectedProviderIds={watchProviders}
        onChange={handleChangeSetFormValues}
        onWebsiteSaved={selectProvider}
        data-testid="can-connect-accordion"
      />
      <Typography variant="subtitle1">
        {formatMessage({ id: 'form.scheduleProviderScreening.summary.screenForRequests' })}
      </Typography>
      <List>
        {templates?.map((template, index) => (
          <ListItem
            key={template.id}
            sx={{
              bgcolor: 'grey.100',
              color: 'text.secondary',
              padding: 2,
              borderTop: index === 0 ? 'none' : `1px solid ${alpha('#000', 0.12)}`,
            }}
          >
            <DescriptionOutlined sx={{ color: 'text.secondary', marginRight: 2 }} fontSize="medium" />
            <ListItemText
              primary={
                <Typography variant="body1" title={template.title}>
                  {template.title}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle1">
          {formatMessage({ id: 'form.scheduleProviderScreening.summary.period' })}
        </Typography>
        <Tooltip
          arrow
          placement="top"
          title={formatMessage({ id: 'form.scheduleProviderScreening.summary.periodTooltip' })}
        >
          <InfoOutlined fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center">
        <Chip label={new Date().getFullYear() - 1} />
      </Stack>
      {!remainingScreeningsEnough && <AiScreeningLimitAlert />}
      <Stack
        border="1px solid"
        borderColor="grey.300"
        px={2}
        py={2}
        direction="row"
        justifyContent="space-between"
        spacing={2}
      >
        <Avatar sx={{ bgcolor: palette.communication.light }}>
          <AutoAwesomeOutlined sx={{ color: palette.communication.dark }} />
        </Avatar>
        <Stack direction="row" justifyContent="space-between" flexGrow={1}>
          <Stack>
            <Typography gutterBottom>
              {formatMessage(
                { id: 'form.scheduleProviderScreening.preScreeningCount.title' },
                { count: totalScreeningsToPerform },
              )}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatMessage(
                { id: 'import.summary.aiScreeningInfoDescription' },
                { count: remainingProviderScreenings },
              )}
            </Typography>
          </Stack>
          <Typography>
            {formatMessage(
              { id: 'form.scheduleProviderScreening.preScreeningCount.description' },
              { companies: watchProviders?.length, questionnaires: templates?.length },
            )}
          </Typography>
        </Stack>
      </Stack>

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mt={2}>
        <FormControlLabel
          control={<Checkbox checked={sendEmailOnComplete} onChange={e => onEmailToggle(e.target.checked)} />}
          label={<Typography variant="body1">{formatMessage({ id: 'form.sendEmailWhenComplete' })}</Typography>}
        />
        <LoadingButton loading={isLoading} variant="contained" onClick={onSubmit} disabled={!remainingScreeningsEnough}>
          {formatMessage({ id: 'form.scheduleProviderScreening.summary.confirm' })}
        </LoadingButton>
      </Box>

      <Divider sx={{ mt: 2 }} />

      <Alert variant="filled" severity="info" sx={{ backgroundColor: 'common.white', alignItems: 'flex-start' }}>
        <AlertTitle>{formatMessage({ id: 'form.scheduleProviderScreening.summary.informationAlertTitle' })}</AlertTitle>
        {formatMessage({ id: 'form.scheduleProviderScreening.summary.informationAlert' })}
      </Alert>
    </Stack>
  )
}

export default ScheduleProviderScreeningSummary
